import { IoAddOutline, IoTrashOutline } from "react-icons/io5";

import * as UI from "../../../modules/ui-kit";

/*
 * The repeating list fields of the taxonomy editors.
 *
 * These were duplicated verbatim between the BimClass and Geometry Type detail
 * editors, and absent from the create forms — so a new entity could only get
 * images, requirement groups, requirements or properties by hand-editing the
 * JSON tab. Every one is a controlled `value` / `onChange` pair over a plain
 * array, so the detail editors (which hold a whole source document) and
 * EntityCreateForm (which holds flat default values) can both drive them.
 */

// the list fields edit loosely-shaped fragments of a taxonomy definition
type ListItem = Record<string, unknown>;

type ListFieldProps<T> = {
    description: string;
    disabled?: boolean;
    label: string;
    onChange: (value: T[]) => void;
    value: T[] | undefined;
};

/** A list of image URLs — Geometry Type `images`, BimClass `previews`. */
export function UrlListField(props: ListFieldProps<string> & { addLabel: string }) {
    const { addLabel, description, disabled, label, onChange, value } = props;
    const urls = value ?? [];

    return (
        <UI.Field>
            <UI.FieldLabel>{ label } ({ urls.length })</UI.FieldLabel>
            <UI.FieldDescription>{ description }</UI.FieldDescription>
            <div className="images-list">
                { urls.map((url: string, idx: number) => (
                    <div className="image-row" key={ idx }>
                        { url && <img alt="" className="img-preview" src={ url } /> }
                        <UI.TextInput
                            disabled={ disabled }
                            onChange={ (val) => onChange(urls.map((u, i) => (i === idx ? val : u))) }
                            placeholder="https://..."
                            value={ url }
                        />
                        <UI.Button
                            $appearance="subtle"
                            $size="small"
                            disabled={ disabled }
                            onClick={ () => onChange(urls.filter((_, i) => i !== idx)) }
                            type="button"
                        >
                            <IoTrashOutline />
                        </UI.Button>
                    </div>
                )) }
                <UI.Button
                    $size="small"
                    disabled={ disabled }
                    onClick={ () => onChange([...urls, ""]) }
                    type="button"
                >
                    <IoAddOutline /> { addLabel }
                </UI.Button>
            </div>
        </UI.Field>
    );
}

/** `requirementGroups` — a stable `key` plus a translated group name. */
export function RequirementGroupsField(props: ListFieldProps<ListItem>) {
    const { description, disabled, label, onChange, value } = props;
    const groups = value ?? [];

    function update(index: number, key: string, val: unknown) {
        onChange(groups.map((group, i) => (i === index ? { ...group, [key]: val } : group)));
    }

    return (
        <UI.Field>
            <UI.FieldLabel>{ label } ({ groups.length })</UI.FieldLabel>
            <UI.FieldDescription>{ description }</UI.FieldDescription>
            <div className="groups-list">
                { groups.map((group, idx) => (
                    <div className="group-card" key={ group.key ?? idx }>
                        <div className="group-header">
                            <UI.TextInput
                                disabled={ disabled }
                                onChange={ (keyVal) => update(idx, "key", keyVal) }
                                placeholder="group_key"
                                value={ group.key ?? "" }
                            />
                            <UI.Button
                                $appearance="subtle"
                                $size="small"
                                disabled={ disabled }
                                onClick={ () => onChange(groups.filter((_, i) => i !== idx)) }
                                type="button"
                            >
                                <IoTrashOutline /> Remove
                            </UI.Button>
                        </div>
                        <UI.TranslatedInput
                            disabled={ disabled }
                            label="group name"
                            onChange={ (nameVal) => update(idx, "name", nameVal) }
                            value={ group.name ?? [] }
                        />
                    </div>
                )) }
                <UI.Button
                    $size="small"
                    disabled={ disabled }
                    onClick={ () => onChange([
                        ...groups,
                        {
                            key: `group_${ groups.length + 1 }`,
                            name: [{ language: "en", text: "New Requirement Group" }],
                            open: true,
                        },
                    ]) }
                    type="button"
                >
                    <IoAddOutline /> Add Requirement Group
                </UI.Button>
            </div>
        </UI.Field>
    );
}

/** BimClass `requirements` — a `key`, the `group` it belongs to, and a name. */
export function RequirementsField(props: ListFieldProps<ListItem>) {
    const { description, disabled, label, onChange, value } = props;
    const requirements = value ?? [];

    function update(index: number, key: string, val: unknown) {
        onChange(requirements.map((req, i) => (i === index ? { ...req, [key]: val } : req)));
    }

    return (
        <UI.Field>
            <UI.FieldLabel>{ label } ({ requirements.length })</UI.FieldLabel>
            <UI.FieldDescription>{ description }</UI.FieldDescription>
            <div className="groups-list">
                { requirements.map((req, idx) => (
                    <div className="group-card" key={ req.key ?? idx }>
                        <div className="group-header">
                            <UI.TextInput
                                disabled={ disabled }
                                onChange={ (keyVal) => update(idx, "key", keyVal) }
                                placeholder="parameter_key"
                                value={ req.key ?? "" }
                            />
                            <UI.TextInput
                                disabled={ disabled }
                                onChange={ (groupVal) => update(idx, "group", groupVal) }
                                placeholder="group_key"
                                value={ req.group ?? "" }
                            />
                            <UI.Button
                                $appearance="subtle"
                                $size="small"
                                disabled={ disabled }
                                onClick={ () => onChange(requirements.filter((_, i) => i !== idx)) }
                                type="button"
                            >
                                <IoTrashOutline /> Remove
                            </UI.Button>
                        </div>
                        <UI.TranslatedInput
                            disabled={ disabled }
                            label="parameter name"
                            onChange={ (nameVal) => update(idx, "name", nameVal) }
                            value={ req.name ?? [] }
                        />
                    </div>
                )) }
                <UI.Button
                    $size="small"
                    disabled={ disabled }
                    onClick={ () => onChange([
                        ...requirements,
                        {
                            key: "",
                            name: [{ language: "en", text: "" }],
                        },
                    ]) }
                    type="button"
                >
                    <IoAddOutline /> Add Parameter / Requirement
                </UI.Button>
            </div>
        </UI.Field>
    );
}

/** `properties` — a parameter reference and the value assigned to it. */
export function PropertiesField(props: ListFieldProps<ListItem>) {
    const { description, disabled, label, onChange, value } = props;
    const properties = value ?? [];

    function update(index: number, keyPath: string, val: unknown) {
        onChange(properties.map((prop: ListItem, i: number) => {
            if (i !== index) return prop;
            if (keyPath === "parameter.id") {
                const parameter = prop.parameter as { ref?: string } | undefined;
                return {
                    ...prop,
                    parameter: {
                        ref: parameter?.ref ?? "Parameter",
                        id: val,
                    },
                };
            }
            return { ...prop, [keyPath]: val };
        }));
    }

    return (
        <UI.Field>
            <UI.FieldLabel>{ label } ({ properties.length })</UI.FieldLabel>
            <UI.FieldDescription>{ description }</UI.FieldDescription>
            <div className="groups-list">
                { properties.map((prop: ListItem, idx: number) => (
                    <div className="group-card" key={ idx }>
                        <div className="group-header">
                            <UI.TextInput
                                disabled={ disabled }
                                onChange={ (idVal) => update(idx, "parameter.id", idVal) }
                                placeholder="Parameter ID (e.g. c852e868-...)"
                                value={ prop.parameter?.id ?? "" }
                            />
                            <UI.TextInput
                                disabled={ disabled }
                                onChange={ (val) => update(idx, "value", val) }
                                placeholder="Value (e.g. CI01)"
                                value={ typeof prop.value === "string" ? prop.value : JSON.stringify(prop.value ?? "") }
                            />
                            <UI.Button
                                $appearance="subtle"
                                $size="small"
                                disabled={ disabled }
                                onClick={ () => onChange(properties.filter((_, i) => i !== idx)) }
                                type="button"
                            >
                                <IoTrashOutline /> Remove
                            </UI.Button>
                        </div>
                    </div>
                )) }
                <UI.Button
                    $size="small"
                    disabled={ disabled }
                    onClick={ () => onChange([
                        ...properties,
                        { parameter: { ref: "Parameter", id: "" }, value: "" },
                    ]) }
                    type="button"
                >
                    <IoAddOutline /> Add Property
                </UI.Button>
            </div>
        </UI.Field>
    );
}
